import { Box, Center, Flex, Group, Loader, Text } from '@mantine/core';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Client } from 'src/admin/users/types';
import { Button as DesignButton } from 'src/design-system/Button';
import { FlowNavigationFooter } from 'src/design-system/FlowNavigationFooter';
import { CollapsibleSearch } from 'src/design-system/CollapsibleSearch';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import { FlowBody } from 'src/UI/generate-flow/FlowBody';
import { FlowLayout } from 'src/UI/generate-flow/FlowLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import CreateClientModal from '../CreateClientModalV2';
import { CrossAdvisorConfirmModal } from './CrossAdvisorConfirmModal';
import { useClients } from '../hooks/useClients';
import { useFamily } from '../hooks/useFamily';
import { CreateFamilyPayload } from '../hooks/useCreateFamily';
import { useUpdateFamily } from '../hooks/useUpdateFamily';
import { getFamilyMemberClientIds } from '../utils';
import {
  roleModelPayloadFromInProgressFamilyV2,
  starModelPayloadFromInProgressFamilyV2,
  V2MemberRelationship,
  V2MemberRoles,
} from './dataHelpers';
import MemberSearchList from './MemberSearchList';
import NameAndRelationshipsStep from './NameAndRelationshipsStep';
import NameAndRolesStep from './NameAndRolesStep';

export default function AddFamilyMemberPage() {
  const { familyId = '' } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { flagEnabledFamilyRoleModel } = useFlags();
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { clients, isLoading: areClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const { updateFamily, isLoading: isSaving } = useUpdateFamily();
  const { showConfirmation } = useGlobalConfirmation();
  const [step, setStep] = useState<'select' | 'details'>('select');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMembers, setNewMembers] = useState<Client[]>([]);
  const [showAddIndividual, setShowAddIndividual] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<CreateFamilyPayload | null>(null);

  const profilePath = `/family/${familyId}/profile`;

  if (isFamilyLoading || areClientsLoading) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  if (!family) {
    return (
      <Center h="100%">
        <Text c="gray.6">This family could not be found.</Text>
      </Center>
    );
  }

  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));
  // Use the full member set (main + secondary + role-model members); relying on
  // secondaryFamilyMembers alone drops members that only exist in `members`,
  // which would silently remove them from the family on the next update.
  const existingMembers = getFamilyMemberClientIds(family)
    .map(id => clientById[id])
    .filter((client): client is Client => client !== undefined);
  const allMembers = [...existingMembers, ...newMembers];

  const initialRoles: V2MemberRoles = {};
  if (family.mainFamilyMember.familyRoleId) {
    initialRoles[family.mainFamilyMember.clientId] = family.mainFamilyMember.familyRoleId;
  }
  family.members.forEach(member => {
    initialRoles[member.clientId] = member.familyRoleId ?? null;
  });

  const initialRelationships: Record<string, V2MemberRelationship> = {};
  family.relationships.forEach(({ fromClientId, toClientId, relationshipType }) => {
    (initialRelationships[fromClientId] ??= {})[toClientId] = relationshipType;
  });

  const toggleMember = (client: Client) => {
    setNewMembers(prev => {
      const isSelected = prev.some(m => m.id === client.id);
      if (isSelected) return prev.filter(m => m.id !== client.id);
      return [...prev, client];
    });
  };

  const handleNewClientAdded = (client: Client) => {
    setNewMembers(prev => [...prev, client]);
    setShowAddIndividual(false);
  };

  const hasCrossAdvisorMember = newMembers.some(
    member => member.advisorUserId && member.advisorUserId !== family.advisorUserId,
  );

  const performUpdate = async (payload: CreateFamilyPayload) => {
    try {
      await updateFamily(familyId, payload);
      showConfirmation(
        newMembers.length === 1
          ? `${newMembers[0].firstName} added to family`
          : `${newMembers.length} members added to family`,
      );
      navigate(`${profilePath}?member=${family.mainFamilyMember.clientId}`);
    } catch (error) {
      notifyAndLogError(
        'Failed to add family member',
        'An error occurred while adding the member. Please try again.',
        error,
      );
    }
  };

  const attemptSubmit = async (payload: CreateFamilyPayload) => {
    if (hasCrossAdvisorMember) {
      setPendingPayload(payload);
      return;
    }
    await performUpdate(payload);
  };

  const handleSubmit = (familyName: string, relationships: Record<string, V2MemberRelationship>) =>
    attemptSubmit(
      starModelPayloadFromInProgressFamilyV2(
        { selectedMembers: allMembers, relationships, name: familyName },
        family.advisorUserId,
      ),
    );

  const handleSubmitRoles = (familyName: string, roles: V2MemberRoles) =>
    attemptSubmit(
      roleModelPayloadFromInProgressFamilyV2(
        { selectedMembers: allMembers, roles, name: familyName },
        family.advisorUserId,
      ),
    );

  if (step === 'details') {
    return (
      <>
        {flagEnabledFamilyRoleModel ? (
          <NameAndRolesStep
            selectedMembers={newMembers}
            onBack={() => setStep('select')}
            onCancel={() => navigate(profilePath)}
            onSubmit={handleSubmitRoles}
            isLoading={isSaving}
            initialFamilyName={family.name}
            initialRoles={initialRoles}
            actionLabel="Add to family"
            title="Set family roles"
          />
        ) : (
          <NameAndRelationshipsStep
            selectedMembers={allMembers}
            onBack={() => setStep('select')}
            onCancel={() => navigate(profilePath)}
            onSubmit={handleSubmit}
            isLoading={isSaving}
            initialFamilyName={family.name}
            initialRelationships={initialRelationships}
            actionLabel="Add to family"
            title="Set relationships"
          />
        )}
        <CrossAdvisorConfirmModal
          opened={pendingPayload !== null}
          onClose={() => setPendingPayload(null)}
          onConfirm={() => {
            if (pendingPayload) performUpdate(pendingPayload);
          }}
          isLoading={isSaving}
          confirmLabel="Add to family"
          description={`Some of these clients belong to a different advisor. Adding them to ${family.name} will move them to this family's advisor.`}
        />
      </>
    );
  }

  return (
    <>
      <Flex direction="column" h="100%">
        <FlowLayout
          header={
            <Box px="xl" pt="xxl" pb="sm">
              <Group justify="space-between" pb="sm">
                <Text c="gray.10" size="displaySm" fw={FONT_WEIGHTS.semibold}>
                  Select clients to include in {family.name}
                </Text>
                <DesignButton variant="text" onClick={() => navigate(profilePath)}>
                  Cancel
                </DesignButton>
              </Group>

              <Group pb="md" gap="sm">
                <CollapsibleSearch
                  open={searchOpen}
                  onOpenChange={setSearchOpen}
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by name, email, address"
                >
                  <DesignButton variant="secondary" onClick={() => setShowAddIndividual(true)}>
                    Add new individual
                  </DesignButton>
                </CollapsibleSearch>
              </Group>
            </Box>
          }
          body={
            <FlowBody
              footerAlign="center"
              footer={
                <FlowNavigationFooter
                  onBack={() => navigate(profilePath)}
                  onAction={() => setStep('details')}
                  actionDisabled={newMembers.length < 1}
                />
              }
            >
              <Box px="xs">
                <MemberSearchList
                  selectedMembers={newMembers}
                  onToggle={toggleMember}
                  searchTerm={searchTerm}
                  noScroll
                  excludeInFamilies
                />
              </Box>
            </FlowBody>
          }
        />
      </Flex>
      <CreateClientModal
        opened={showAddIndividual}
        close={() => setShowAddIndividual(false)}
        navigateAfterCreation={false}
        onClientCreated={handleNewClientAdded}
      />
    </>
  );
}
